import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import api from '../../utils/api';
import CropCard from '../../components/crops/CropCard';
import BackButton from '../../components/common/BackButton';

const FILTERS = [
  { id:'all',           label:'All' },
  { id:'growing',       label:' Growing' },
  { id:'ready_to_sell', label:'🟢 Ready' },
  { id:'sold',          label:'✅ Sold' },
  { id:'closed',        label:'🔒 Closed' },
];

const STATUS_MAP = {
  growing:       { bg:'#DCFCE7', color:'#166534', label:' Growing' },
  ready_to_sell: { bg:'#D1FAE5', color:'#065F46', label:'🟢 Ready to Sell' },
  sold:          { bg:'#DBEAFE', color:'#1E40AF', label:'✅ Sold' },
  closed:        { bg:'#F3F4F6', color:'#6B7280', label:'🔒 Closed' },
};

const ACTIONS = [
  { status:'growing',       label:' Back to Growing',    desc:'Crop is still growing' },
  { status:'ready_to_sell', label:'🟢 Mark Ready to Sell', desc:'Ready for buyers' },
  { status:'sold',          label:'✅ Mark as Sold',        desc:'Crop has been sold' },
  { status:'closed',        label:'🔒 Close Crop',          desc:'Stop showing this crop' },
];

// ── Status change bottom sheet ────────────────────────────────
function StatusSheet({ crop, onClose, onUpdated }) {
  const [saving, setSaving] = useState('');

  const changeStatus = async (status) => {
    setSaving(status);
    try {
      const { data } = await api.put(`/crops/${crop._id}/status`, { status });
      toast.success(`Updated to ${STATUS_MAP[status]?.label || status}`);
      onUpdated(crop._id, status);
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Error updating status');
    } finally { setSaving(''); }
  };

  return (
    <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.55)', zIndex:1000, display:'flex', alignItems:'flex-end' }}
      onClick={onClose}>
      <div style={{ background:'white', borderRadius:'16px 16px 0 0', width:'100%', maxWidth:480, margin:'0 auto', padding:20 }}
        onClick={e => e.stopPropagation()}>

        {/* Header */}
        <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:16 }}>
          <div>
            <div style={{ fontSize:15, fontWeight:700, color:'#1a2e05' }}>{crop.cropName}</div>
            <div style={{ fontSize:12, color:'#64748B' }}>Change status</div>
          </div>
          <button onClick={onClose}
            style={{ background:'#F1F5F9', border:'none', borderRadius:'50%', width:32, height:32, fontSize:18, cursor:'pointer' }}>
            ×
          </button>
        </div>

        {/* Current status */}
        <div style={{ display:'flex', alignItems:'center', gap:8, padding:'8px 12px', background:'#F8FAFC', borderRadius:8, marginBottom:14, border:'1px solid #E2E8F0' }}>
          <span style={{ fontSize:12, color:'#64748B' }}>Current:</span>
          <span style={{ background:STATUS_MAP[crop.status]?.bg, color:STATUS_MAP[crop.status]?.color, borderRadius:20, padding:'3px 10px', fontSize:12, fontWeight:600 }}>
            {STATUS_MAP[crop.status]?.label || crop.status}
          </span>
        </div>

        {/* Options — show all except current */}
        <div style={{ display:'flex', flexDirection:'column', gap:8 }}>
          {ACTIONS.filter(a => a.status !== crop.status).map(action => (
            <button key={action.status}
              onClick={() => changeStatus(action.status)}
              disabled={!!saving}
              style={{
                display:'flex', alignItems:'center', gap:12,
                padding:'12px 14px', borderRadius:10, cursor:'pointer',
                background: saving === action.status ? '#F0FDF4' : 'white',
                border: `1.5px solid ${saving === action.status ? '#1A6B3C' : '#E2E8F0'}`,
                opacity: saving && saving !== action.status ? 0.5 : 1,
                transition:'all 0.15s',
              }}>
              <div style={{ flex:1, textAlign:'left' }}>
                <div style={{ fontSize:14, fontWeight:600, color:'#1a2e05' }}>{action.label}</div>
                <div style={{ fontSize:11, color:'#64748B', marginTop:1 }}>{action.desc}</div>
              </div>
              {saving === action.status
                ? <div style={{ width:18, height:18, border:'2px solid #E2E8F0', borderTopColor:'#1A6B3C', borderRadius:'50%', animation:'spin 0.7s linear infinite' }} />
                : <span style={{ color:'#CBD5E1', fontSize:18 }}>›</span>
              }
            </button>
          ))}
        </div>

        <button onClick={onClose}
          style={{ width:'100%', marginTop:12, padding:'10px', background:'#F1F5F9', border:'none', borderRadius:10, fontSize:13, color:'#64748B', cursor:'pointer' }}>
          Cancel
        </button>
      </div>
    </div>
  );
}

// ── Crop card with tappable status badge ──────────────────────
function MyCropCard({ crop, onStatusTap }) {
  const navigate = useNavigate();
  const st       = STATUS_MAP[crop.status] || STATUS_MAP.growing;
  const stage    = crop.latestStage || crop.stages?.[crop.stages?.length - 1];
  const thumb    = stage?.mediaUrls?.find(u => u && !u.startsWith('data:'));

  return (
    <div className="card" style={{ marginBottom:12, overflow:'hidden' }}>
      {/* Thumbnail */}
      <div style={{ position:'relative', height:160, background:'#F0FDF4', cursor:'pointer' }}
        onClick={() => navigate(`/crop/${crop._id}`)}>
        {thumb
          ? <img src={thumb} alt={crop.cropName} style={{ width:'100%', height:'100%', objectFit:'cover' }} />
          : <div style={{ width:'100%', height:'100%', display:'flex', alignItems:'center', justifyContent:'center', fontSize:48 }}></div>
        }
        <div style={{ position:'absolute', bottom:8, right:8, background:'rgba(0,0,0,0.6)', color:'white', borderRadius:10, padding:'2px 8px', fontSize:12, fontWeight:600 }}>
          Day {crop.currentDayCount || 0}
        </div>
        {/* Tappable status badge */}
        <button
          onClick={e => { e.stopPropagation(); onStatusTap(crop); }}
          style={{ position:'absolute', top:8, left:8, background:st.bg, color:st.color, border:`1.5px solid ${st.color}40`, borderRadius:20, padding:'4px 10px', fontSize:12, fontWeight:600, cursor:'pointer', display:'flex', alignItems:'center', gap:4 }}>
          {st.label}
          <span style={{ fontSize:10, opacity:0.7 }}>▼</span>
        </button>
      </div>

      {/* Body */}
      <div style={{ padding:'10px 14px', cursor:'pointer' }} onClick={() => navigate(`/crop/${crop._id}`)}>
        <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start' }}>
          <div>
            <div style={{ fontWeight:700, fontSize:15, color:'#1a2e05' }}>{crop.cropName}</div>
            <div style={{ fontSize:12, color:'#64748B', marginTop:1, textTransform:'capitalize' }}>
              {crop.category} · {crop.subCategory}
            </div>
          </div>
        </div>
        {stage?.description && (
          <p style={{ fontSize:13, color:'#475569', marginTop:6, lineHeight:1.4,
            overflow:'hidden', display:'-webkit-box', WebkitLineClamp:2, WebkitBoxOrient:'vertical' }}>
            {stage.description}
          </p>
        )}
        <div style={{ display:'flex', gap:12, marginTop:8, fontSize:12, color:'#94A3B8' }}>
          <span>❤️ {crop.likeCount || 0}</span>
          <span>💬 {crop.commentCount || 0}</span>
          <span>⭐ {crop.bestCropCount || 0}</span>
          <span style={{ marginLeft:'auto' }}>
            {crop.stages?.length || 1} stage{(crop.stages?.length || 1) !== 1 ? 's' : ''}
          </span>
        </div>
      </div>
    </div>
  );
}

// ── Main Page ─────────────────────────────────────────────────
export default function MyCropsPage() {
  const navigate = useNavigate();
  const [crops, setCrops]       = useState([]);
  const [loading, setLoading]   = useState(true);
  const [filter, setFilter]     = useState('all');
  const [statusCrop, setStatusCrop] = useState(null);

  useEffect(() => { load(); }, [filter]);

  const load = async () => {
    setLoading(true);
    try {
      const q = filter !== 'all' ? `?status=${filter}` : '';
      const { data } = await api.get(`/crops/my${q}`);
      setCrops(data.crops || []);
    } catch {} finally { setLoading(false); }
  };

  // Update crop status in list instantly
  const handleStatusUpdated = (cropId, newStatus) => {
    setCrops(prev => {
      const updated = prev.map(c =>
        c._id === cropId ? { ...c, status: newStatus } : c
      );
      // Remove from list if filter active and status no longer matches
      if (filter !== 'all') {
        return updated.filter(c => c.status === filter);
      }
      return updated;
    });
  };

  return (
    <div className="page">
      <div className="header header--simple">
        <BackButton />
        <div className="header__title"> My Crops</div>
        <button className="header__action" onClick={() => navigate('/add-crop')}>+ Add</button>
      </div>

      {/* Filter pills */}
      <div className="pill-bar">
        {FILTERS.map(f => (
          <button key={f.id} className={`pill${filter === f.id ? ' pill--active' : ''}`}
            onClick={() => setFilter(f.id)}>
            {f.label}
          </button>
        ))}
      </div>

      <div className="container" style={{ paddingTop:'var(--sp-4)' }}>
        {loading ? (
          <div className="full-center"><span className="spinner spinner--lg" /></div>
        ) : crops.length === 0 ? (
          <div className="empty-state">
            <div className="empty-state__icon"></div>
            <div className="empty-state__title">No crops found</div>
            <div className="empty-state__desc">Add your first crop to get started</div>
            <button className="btn btn--primary" onClick={() => navigate('/add-crop')}>+ Add New Crop</button>
          </div>
        ) : (
          crops.map(crop => (
            <MyCropCard
              key={crop._id}
              crop={crop}
              onStatusTap={c => setStatusCrop(c)}
            />
          ))
        )}
        <div style={{ height:'var(--sp-8)' }} />
      </div>

      {/* Status sheet */}
      {statusCrop && (
        <StatusSheet
          crop={statusCrop}
          onClose={() => setStatusCrop(null)}
          onUpdated={handleStatusUpdated}
        />
      )}
    </div>
  );
}